/**
 * Colours used only by the Coach Resume optimization UI.
 *
 * Kept out of the theme tokens on purpose: `text-error` / `text-success` are the builder's own
 * semantic colours and are used across the whole app, so retuning them for this one screen would
 * change every toast, validation message and status pill with it. These are literal values scoped to
 * the coach suggestion cards.
 *
 * The pair is a diff, not a status. `removed` is the résumé's current wording being struck out and
 * `added` is the replacement — both are softer than the semantic red/green because they sit in long
 * paragraphs of body text that has to stay comfortable to read, not alarm anybody.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const COACH_DIFF_COLORS = {
  /** Current résumé text, struck through. */
  removed: "#81423A",
  /** The AI's replacement text. */
  added: "#5F9D74",
} as const;

/**
 * Tailwind arbitrary-value classes for the replacement colour.
 *
 * `RichInput` renders a Tiptap editor whose content is styled through `prose`, so a `style` prop on
 * the wrapper is overridden by the prose colour rules. The `prose-p` / `prose-li` variants are the
 * ones that actually reach the rendered paragraphs and list items inside the editor.
 */
export const COACH_ADDED_TEXT_CLASSES =
  "text-[#5F9D74] prose-p:text-[#5F9D74] prose-li:text-[#5F9D74] prose-strong:text-[#5F9D74]";

/**
 * Palette for the optimization INTAKE screen (`/coach/optimize`).
 *
 * ⚠️ DELIBERATELY NOT THEME TOKENS. The screen previously used `text-primary` for the active
 * spinner and `text-success` for the completed ticks. Both are builder-wide semantic colours, so
 * the intake screen inherited whatever the app theme happened to be — the ticks came out the
 * builder's green and the spinner the theme's primary, which is not the identity this screen is
 * supposed to have, and retuning the tokens to fix it would repaint every toast and status pill in
 * the app.
 *
 * Amber, because this screen is a WAIT, not a result. Green ticks on a checklist that is still
 * running read as "five things succeeded" when what has actually happened is "five things are done
 * and the slow one is still going". Amber says in-progress for the whole panel, and the one signal
 * that matters — the spinner on the active row — is the same hue, so nothing competes with it.
 */
export const COACH_INTAKE_COLORS = {
  /** Completed step ticks, the active spinner, and the job title in the heading. */
  accent: "#EDB55A",
  /** Slightly deeper, for the tick's filled circle so the glyph inside stays legible. */
  accentStrong: "#D99B32",
  /** The scanning sweep over the résumé preview. */
  scan: "#5AF28E",
} as const;
